/**
 * effects/ongoing.ts — «Постоянно» (D53+): детерминированный реконсилятор
 * производных непрерывных StatMod-аур.
 *
 * Настоящий code-native контракт, а не UI-метка и не per-card onAnyEvent. Пока
 * источник жив и на поле, его чистый провайдер `hooks.ongoing(PureContext)`
 * ДЕТЕРМИНИРОВАННО выводит из публичного snapshot набор аур (scope + statMods).
 * Реконсилятор синхронизирует их как обычные effects-ДАННЫЕ (в GameState нет
 * функций), а `effectiveStats` слоит их в существующем каноническом порядке.
 * Ауры source-bound: при уходе/смене контроллера/локации источника они исчезают
 * или пересчитываются автоматически. Реконсилятор вызывается движком один раз
 * после события (registry/resolution), а не подпиской карты на каждое событие.
 */
import { buildCardContext } from '../ctx/context.js';
import { isLiveUnit } from '../registry/subscribers.js';
/** Маркер в `effect.vars`: производная ongoing-аура с ключом `${sourceId}::${abilityId}`. */
const ONGOING_MARK = '__ongoing';
/** Есть ли в реестре хоть один провайдер `ongoing` (иначе реконсиляция — no-op). */
export function hasOngoingProviders(registry) {
    for (const def of registry.values()) {
        if (typeof def.hooks?.ongoing === 'function')
            return true;
    }
    return false;
}
/** Каноническая сериализация ауры для сравнения (scope + statMods + публичные vars). */
function auraShape(scope, statMods, vars) {
    const mods = statMods.map((mod) => `${mod.stat}:${mod.op}:${mod.value}`).join(',');
    const publicVars = Object.keys(vars).sort()
        .filter((key) => key !== ONGOING_MARK)
        .map((key) => `${key}=${JSON.stringify(vars[key])}`).join(',');
    return `${JSON.stringify(scope)}|${mods}|${publicVars}`;
}
function sameAura(effect, desired) {
    return auraShape(effect.scope, effect.statMods ?? [], effect.vars)
        === auraShape(desired.scope, desired.statMods, desired.vars);
}
/**
 * Синхронизировать производные ongoing-ауры с текущим snapshot: снять устаревшие
 * (источник ушёл / условие пропало / изменились statMods), добавить новые. Диф
 * детерминирован; снятия — по effect.seq, добавления — в каноническом порядке
 * источников (seq/id) и порядке contributions провайдера.
 */
export function reconcileOngoing(draft, registry, ops) {
    const liveSources = Object.values(draft.instances)
        .filter((inst) => isLiveUnit(draft, inst))
        .sort((a, b) => a.seq - b.seq || a.id.localeCompare(b.id));
    const desired = new Map();
    for (const source of liveSources) {
        const hooks = registry.get(source.defId)?.hooks;
        if (typeof hooks?.ongoing !== 'function')
            continue;
        const ctx = buildCardContext(draft, ops, { selfId: source.id }, undefined, registry);
        for (const contribution of hooks.ongoing(ctx) ?? []) {
            // Не материализуем unit-ауру на носителе, которого уже нет на поле: иначе
            // sweepEffects снимет её (носитель ушёл), а следующая реконсиляция снова
            // добавит — детерминированная осцилляция до общего event-limit. Реконсилятор
            // остаётся согласован со sweep GC.
            if (contribution.scope.kind === 'unit') {
                const carrier = draft.instances[contribution.scope.instanceId];
                if (carrier === undefined || !isLiveUnit(draft, carrier))
                    continue;
            }
            const key = `${source.id}::${contribution.abilityId}`;
            if (desired.has(key)) {
                throw new Error(`reconcileOngoing: дубликат ongoing abilityId '${contribution.abilityId}' у '${source.id}'`);
            }
            desired.set(key, {
                key, sourceId: source.id, abilityId: contribution.abilityId, defId: source.defId,
                scope: contribution.scope, statMods: contribution.statMods, vars: contribution.vars ?? {},
            });
        }
    }
    const existing = new Map();
    for (const effect of draft.effects) {
        const mark = effect.vars[ONGOING_MARK];
        if (typeof mark === 'string')
            existing.set(mark, effect);
    }
    const toRemove = [];
    for (const [key, effect] of existing) {
        const want = desired.get(key);
        if (want === undefined || !sameAura(effect, want))
            toRemove.push(effect);
    }
    toRemove.sort((a, b) => a.seq - b.seq);
    for (const effect of toRemove)
        ops.removeEffect(effect.id);
    for (const aura of desired.values()) {
        const current = existing.get(aura.key);
        if (current !== undefined && sameAura(current, aura) && !toRemove.includes(current))
            continue;
        ops.addEffect({
            defId: aura.defId,
            scope: structuredClone(aura.scope),
            sourceId: aura.sourceId,
            statMods: aura.statMods.map((mod) => ({ ...mod })),
            vars: { ...structuredClone(aura.vars), [ONGOING_MARK]: aura.key },
        });
    }
}
//# sourceMappingURL=ongoing.js.map